/**
 * Launchpad — the DIRECTIONAL impulse pad (Brief 014). SHARED across Sonic (dash-pad)
 * and N++ (launch pad): the angled cousin of SPRING (which is vertical-only).
 *
 * A SOLID, tile-aligned 1×1 entity. On a top-landing it fires the player along
 * `props.dir` (8-way) at `props.power` (px/s), then ignores re-contacts for a short
 * integer cooldown so a horizontal launch can't re-trigger into a jitter loop.
 *
 * Determinism: a fixed 8-way unit-vector table (literals, no trig) + min arithmetic +
 * an integer cooldown counter → ESLint-clean under src/engine/.
 */
import type { Entity, EntityContext } from '../Entity';
import { EntityType } from '../LevelFormat';
import type { AABB } from '../TileGrid';

const DEFAULT_POWER = 1100;
const FIRE_COOLDOWN_STEPS = 12; // swallow the immediate re-land so it fires once per arrival
const DIAG = 0.7071;            // 1/√2 baked as a literal (no Math.sqrt call needed)

/** 8-way unit vectors. Screen-space y is DOWN, so "up" is negative y. */
const DIRS: Record<string, { ux: number; uy: number; face: 1 | -1 }> = {
  up:        { ux: 0,     uy: -1,    face: 1 },
  down:      { ux: 0,     uy: 1,     face: 1 },
  left:      { ux: -1,    uy: 0,     face: -1 },
  right:     { ux: 1,     uy: 0,     face: 1 },
  upLeft:    { ux: -DIAG, uy: -DIAG, face: -1 },
  upRight:   { ux: DIAG,  uy: -DIAG, face: 1 },
  downLeft:  { ux: -DIAG, uy: DIAG,  face: -1 },
  downRight: { ux: DIAG,  uy: DIAG,  face: 1 },
};

export class Launchpad implements Entity {
  readonly type = EntityType.LAUNCHPAD;
  body: AABB;
  vx = 0;
  vy = 0;
  facing: 1 | -1 = 1;
  alive = true;

  private power: number;
  private dir: { ux: number; uy: number; face: 1 | -1 };
  private cooldown = 0;
  private flash = 0;

  /** @param props `dir` (8-way; default 'up'), `power` (px/s; default 1100). */
  constructor(cx: number, feetY: number, props: Record<string, unknown> | undefined, ts: number) {
    this.body = { x: cx - ts / 2, y: feetY - ts, w: ts, h: ts };
    const p = props?.power;
    this.power = typeof p === 'number' && p > 0 ? p : DEFAULT_POWER;
    const d = typeof props?.dir === 'string' ? props.dir : 'up';
    this.dir = DIRS[d] ?? DIRS.up;
    this.facing = this.dir.face;
  }

  solidBody(): AABB { return this.body; }

  update(_dt: number, _ctx: EntityContext): void {
    if (this.cooldown > 0) this.cooldown--;
    if (this.flash > 0) this.flash--;
  }

  /** Top-landing → fling along `dir`. Cooldown-gated so it fires once per arrival. */
  onStand(ctx: EntityContext, _landingVy: number): void {
    if (this.cooldown > 0) return;
    ctx.player.vx = this.dir.ux * this.power;
    ctx.player.vy = this.dir.uy * this.power;
    ctx.player.grounded = false; // leave the pad so the fling commits
    this.cooldown = FIRE_COOLDOWN_STEPS;
    this.flash = 8;
    ctx.playSound('spring', 0.6); // POLISH_01 #1 — dedicated launch/boing clip
    // POLISH_01 #10 — the biggest velocity events got the weakest feedback. Burst at the pad,
    // a launch-direction dust fan at the feet, and a body squash + trauma via launchFx.
    const cx = this.body.x + this.body.w / 2;
    const cy = this.body.y + this.body.h / 2;
    ctx.emitFx?.('burst', cx, cy, { count: 6, color: 0xffe66d });
    const pb = ctx.player.body;
    ctx.emitFx?.('dust', pb.x + pb.w / 2, pb.y + pb.h, { count: 6, dir: this.dir.ux >= 0 ? 1 : -1 });
    ctx.launchFx?.(this.dir.ux, this.dir.uy);
  }

  onPlayerOverlap(_ctx: EntityContext): void { /* solid — acts via onStand */ }

  spriteKey(): string { return this.flash > 0 ? 'launchpad_fire' : 'launchpad'; }

  visible(): boolean { return true; }
}
